import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import FormItem from 'components/form/FormItem';
import Collapse from 'components/global/Collapse';
import Loading from 'components/global/Loading';
import Radio from 'components/global/Radio';
import Typography from 'components/global/Typography';
import { ModalActions } from 'components/global/styled/Modal.styled';
import AddCardPayTrButton from 'components/payment/paytr/AddCardPayTrButton';
import PaytrIframe from 'components/payment/paytr/PaytrIframe';
import AddCardTapPaymentButton from 'components/payment/tap/AddCardTapPaymentButton';
import TapIframe from 'components/payment/tap/TapIframe';

import useLocale from 'hooks/useLocale';

import { PAYMENT_GATEWAY_TYPE, PAYMENT_METHODS } from 'utils/constants';

import { ModalContentWrapper, PriceContainer } from '../../SubscriptionAccount/SubscriptonAccountModal.styled';
import * as S from './AddPaymentMethod.styled';

const ADD_PAYMENT_METHOD_PRICE = 1;

const PaymentModal = ({
	closeModal,
	nextStep,
	setCardLastFour,
	confirmationText = 'confirmNewPaymentMethod',
	informationText = 'addPaymentMethodInformation',
	confirmButtonKey = 'completeAddPaymentMethod'
}) => {
	const { t } = useTranslation();
	const { Currency } = useLocale();
	const [selectedRadio, setSelectedRadio] = useState(PAYMENT_METHODS.CREDIT_CARD);
	const [creditInputsValid, setCreditInputsValid] = useState(false);
	const [isIframeLoaded, setIsIframeLoaded] = useState(false);
	const {
		localization: { paymentGatewayType }
	} = useSelector((state) => state.user);

	const radioChange = ({ currentTarget: { id } }) => {
		setSelectedRadio(id);
	};

	const hasPayTRCardValidation = useMemo(
		() => paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR && selectedRadio === PAYMENT_METHODS.CREDIT_CARD,
		[paymentGatewayType, selectedRadio]
	);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					cardHolderName: yup
						.string()
						.nullable()
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					cardNumber: yup
						.string()
						.nullable()
						.min(16)
						.max(20)
						.transform((value) => value?.replace(/\s/g, ''))
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					cvv: yup
						.string()
						.nullable()
						.min(3)
						.max(3)
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					cardExpiry: yup
						.string()
						.nullable()
						.matches(/^(0[1-9]|1[0-2])\/[2-9]\d$/g, t('validation.invalidExpiryDate'))
						.when([], {
							is: () => hasPayTRCardValidation,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						})
				})
				.required(),
		[hasPayTRCardValidation]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			cardHolderName: null,
			cardNumber: null,
			cvv: null,
			cardExpiry: null
		}
	});

	useEffect(() => {
		if (paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR) {
			setIsIframeLoaded(true);
		}
	}, [paymentGatewayType]);

	return (
		<FormProvider {...formMethods}>
			<ModalContentWrapper>
				<PriceContainer>
					<FormItem>
						<Typography className="mb-3" fontWeight="500" color="var(--content-3)">
							{confirmationText}
						</Typography>

						<Typography variant="p2" fontWeight="500">
							{t(informationText, {
								price: ADD_PAYMENT_METHOD_PRICE,
								currency: t(Currency)
							})}
						</Typography>
					</FormItem>
				</PriceContainer>
				<S.PaymentCard id={PAYMENT_METHODS.CREDIT_CARD} onClick={radioChange}>
					<Radio checked={selectedRadio === PAYMENT_METHODS.CREDIT_CARD} readOnly />
					<Typography variant="p1" component="span" fontWeight={500}>
						creditcard
					</Typography>
				</S.PaymentCard>
				<Collapse
					visible={isIframeLoaded && selectedRadio === PAYMENT_METHODS.CREDIT_CARD}
					invisibleMode
					defaultVisible>
					{!isIframeLoaded && <Loading />}
					{[PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE, PAYMENT_GATEWAY_TYPE.TAP_PAYMENT].includes(
						paymentGatewayType
					) && (
						<TapIframe
							subscription={true}
							setIsIframeLoaded={setIsIframeLoaded}
							setInputsValid={(isValid) => setCreditInputsValid(isValid)}
						/>
					)}

					{paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR && (
						<S.CreditCardContainer>
							<PaytrIframe subscription={true} />
						</S.CreditCardContainer>
					)}
				</Collapse>
			</ModalContentWrapper>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={closeModal} />
				{(paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT ||
					paymentGatewayType === PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE) && (
					<AddCardTapPaymentButton
						setCardLastFour={setCardLastFour}
						nextStep={nextStep}
						confirmButtonKey={confirmButtonKey}
						disabled={!creditInputsValid}
					/>
				)}
				{paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR && (
					<AddCardPayTrButton nextStep={nextStep} setCardLastFour={setCardLastFour} />
				)}
			</ModalActions>
		</FormProvider>
	);
};

export default PaymentModal;
